/**
 * What the tab bar and the sidebar share: the destinations, how each one is
 * reached, the ⌘K hint and the desktop breakpoint.
 *
 * Every move goes through T10's layer helpers, never `history.pushState`:
 * the router owns history, and Back must close layers in the order they
 * opened (plan D02). A TAB switch is a REPLACE (`D193`): Back from a tab
 * lands on Today, and Back from Today leaves. The workout is still a PUSH
 * from the central button, so Back from it returns to where it was opened.
 */
import {
  "shell.search_shortcut_mac" as shortcutMac,
  "shell.search_shortcut_other" as shortcutOther,
  "shell.tab_coach" as tabCoach,
  "shell.tab_plan" as tabPlan,
  "shell.tab_profile" as tabProfile,
  "shell.tab_progress" as tabProgress,
  "shell.tab_today" as tabToday,
} from "@repo/i18n";
import {
  ChartNoAxesColumn,
  CircleUserRound,
  House,
  type LucideIcon,
  MessageCircle,
  Rows3,
} from "lucide-react";
import { useCallback, useSyncExternalStore } from "react";
import {
  closeFallbackFor,
  useAppSearch,
  useCloseLayer,
  useOpenLayer,
  useReplaceLayer,
} from "../../app/layers";
import { scrollScreenToTop } from "../../app/screen-stack";
import { type Screen, screenOf } from "../../app/search";

/** A destination: a screen, or the trainer, which opens OVER the screen. */
export type Destination = Screen | "coach";

export interface Tab {
  readonly icon: LucideIcon;
  readonly id: Destination;
  readonly label: () => string;
}

/**
 * The four tabs around the central button (`D218`): Hoje · Plano · [Treinar]
 * · Progresso · Perfil. The trainer is not a tab any more — it can be
 * wanted over any screen, mid-workout included, so it floats
 * (`coach-bubble.tsx`) — and the person took its place.
 */
export const TABS: readonly [Tab, Tab, Tab, Tab] = [
  { id: "today", label: tabToday, icon: House },
  { id: "plan", label: tabPlan, icon: Rows3 },
  { id: "progress", label: tabProgress, icon: ChartNoAxesColumn },
  { id: "profile", label: tabProfile, icon: CircleUserRound },
];

/** The trainer's row on the desktop sidebar, which docks the panel. */
export const COACH_TAB: Tab = {
  id: "coach",
  label: tabCoach,
  icon: MessageCircle,
};

const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** "⌘K" on Apple platforms, "Ctrl K" elsewhere (A2 `nav.tsx:27-29`). */
export function searchShortcut(): string {
  return IS_MAC ? shortcutMac() : shortcutOther();
}

// ── Desktop breakpoint ────────────────────────────────────────────────────────

/** Tailwind's `lg`: the sidebar replaces the tab bar, the trainer docks. */
export const DESKTOP_QUERY = "(min-width: 1024px)";

function subscribeDesktop(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) {
    return () => undefined;
  }
  const list = window.matchMedia(DESKTOP_QUERY);
  list.addEventListener("change", onChange);
  return () => list.removeEventListener("change", onChange);
}

function readDesktop(): boolean {
  return typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia(DESKTOP_QUERY).matches
    : false;
}

export function useIsDesktop(): boolean {
  return useSyncExternalStore(subscribeDesktop, readDesktop, () => false);
}

/** The docked trainer's width on desktop (A2 `index.tsx:60`). */
export const COACH_DOCK_PX = 440;

/** `--coach-dock`: the panel's width while it is docked, else `0px`. */
export function coachDockWidth(coachOpen: boolean, desktop: boolean): string {
  return coachOpen && desktop ? `${COACH_DOCK_PX}px` : "0px";
}

// ── Moves ─────────────────────────────────────────────────────────────────────

/**
 * The navigation state and moves. `screen` is `today` for an absent param,
 * the same reading the router's shell uses.
 */
export function useNavigation() {
  const search = useAppSearch();
  const openLayer = useOpenLayer();
  const replaceLayer = useReplaceLayer();
  const closeLayer = useCloseLayer();
  const screen = screenOf(search);
  const coachOpen = search.overlay === "coach";

  /**
   * A screen tab, or "Personal", which opens the trainer over the screen.
   *
   * A screen tab REPLACES (`D193`): the tabs are siblings, not a stack, so
   * Back from a tab goes to Today and Back from Today leaves the app — what
   * Material asks of a bottom bar. When tabs pushed, Back walked through
   * every tab visited. The tab already active scrolls its screen to the top.
   */
  const go = useCallback(
    (id: Destination) => {
      if (id === "coach") {
        if (search.overlay !== "coach") {
          openLayer({ overlay: "coach" });
        }
        return;
      }
      if (id === screenOf(search)) {
        scrollScreenToTop(id);
        return;
      }
      // `today` is the absent param, so the URL stays bare on Today.
      replaceLayer({ screen: id === "today" ? undefined : id });
    },
    [openLayer, replaceLayer, search]
  );

  /** The sidebar's trainer row toggles the docked panel. */
  const toggleCoach = useCallback(() => {
    if (search.overlay === "coach") {
      closeLayer(closeFallbackFor("overlay"));
    } else {
      openLayer({ overlay: "coach" });
    }
  }, [closeLayer, openLayer, search.overlay]);

  /**
   * The central button. Idle, it starts a workout through the readiness
   * check (`start=1`, the one-shot T54 consumes); with a session running it
   * only brings the workout back.
   */
  const train = useCallback(
    (live: boolean) => {
      openLayer(
        live ? { screen: "workout" } : { screen: "workout", start: "1" }
      );
    },
    [openLayer]
  );

  const openSearch = useCallback(() => {
    openLayer({ overlay: "search", section: undefined, edit: undefined });
  }, [openLayer]);

  // The person's tab (`D218`), from wherever a door to the profile sits.
  const openProfile = useCallback(() => go("profile"), [go]);

  // The gear: the app's settings and the account (`D217`, `D218`) — the
  // person's sheet and the app's configuration have different doors.
  const openSettings = useCallback(() => {
    openLayer({ edit: undefined, overlay: "profile", section: "settings" });
  }, [openLayer]);

  return {
    coachOpen,
    go,
    openProfile,
    openSearch,
    openSettings,
    screen,
    toggleCoach,
    train,
  };
}
